import React from 'react';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import { connect } from "react-redux";
import FormControl from "@mui/material/FormControl";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Switch from '@mui/material/Switch';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import {toast} from "react-toastify";


class PushSettings extends React.Component {

  buildSettingsMap = () => {
    const pushSettingArray = [
      { name: 'selectAll', id: 0, label: 'Select All' },
      { name: 'names', id: 1, label: 'Names & IDs' },
      { name: 'targeting', id: 2, label: 'Standard & Custom Targeting' },
      { name: 'dates', id: 3, label: 'Dates' },
      { name: 'rates', id: 4, label: 'Rates & Fees' },
      { name: 'geos', id: 5, label: 'Geo Targeting' },
    ];
    return new Map(pushSettingArray.map(obj => [obj.id, obj]));
  }

  state = {
    pushSettings: [],
    psMap: this.buildSettingsMap(),
    user: localStorage.getItem('user'),
    soapLog: localStorage.getItem('soapLog'),
    gamDirect: localStorage.getItem('gamDirect'),
    forceStart: localStorage.getItem('forceStart')
  };

  /**
   * On the first call, get the push settings from the order if present.
   */
  componentDidMount() {
    document.title = 'GALILEO - Configure push';
    let ps = [];
    this.setState({ pushSettings: ps });
  }

  /**
   * Push to GAM.
   * could be the whole order, selected placements from order, selected packages from order, or selected placements from package.
   * Note the timeout to let the selected items get updated.
   */
  pushToGAM = () => {
    const { onClose, pushType, onPushOrder, onPushPackages, pushPlacementsFromOrder, pushPlacementsFromPackage, onUploadSelected } = this.props;
    const { pushSettings } = this.state;
    if (pushType === 'order') {
      onPushOrder(pushSettings);
      onClose();
      return;
    }
    const pids = onUploadSelected();
    setTimeout(() => {
      console.log('PS: push to GAM', pushType, pids);
      if (pushType === 'packages') {
        onPushPackages(pushSettings);
      } else if (pushType === 'placements') {
        pushPlacementsFromOrder(pushSettings);
      } else if (pushType === 'placements_package') {
        pushPlacementsFromPackage(pushSettings);
      } else {
        const err = `Invalid push type ${pushType}`;
        toast.error(err, { theme: 'colored', toastId: err });
      }
    }, 1000);
    onClose();
  }

  /**
   * Manage the checkboxes to select fields for pushing.
   * 'Select All' over-rides the individual selections.
   * @param evt - selected value
   */
  handleChange = evt => {
    const { pushSettings, psMap } = this.state;
    const selected = evt.target.value;
    let ps;
    if (pushSettings && pushSettings.length > 0) {
      ps = pushSettings;
    } else {
      ps = [];
    }
    const existing = ps.find(p => p === selected);
    if (selected === 'selectAll' && !existing) {
      ps = [];
      for (let i = 0; i < psMap.size; i++) {
        ps.push(psMap.get(i).name);
      }
    } else if (selected === 'selectAll' && existing) {
      ps = [];
    } else if (existing) {
      ps = ps.filter(p => p !== selected);
    } else {
      ps.push(selected);
    }
    this.setState({ pushSettings: ps });
  }

  getCheckbox = (checked, value) => {
    const isChecked = checked || false;
    return (
      <Checkbox
        checked={isChecked}
        color="primary"
        onChange={this.handleChange}
        value={value}
      />
    )
  };

  handleChangeDirect = () => {
    const { gamDirect } = this.state;
    const gd = !gamDirect;
    this.setState({ gamDirect: gd });
    if (gd) {
      localStorage.setItem('gamDirect', 'true');
    } else {
      localStorage.removeItem('gamDirect');
    }
  }

  handleSoapLog = () => {
    const { soapLog } = this.state;
    const sl = !soapLog;
    this.setState({ soapLog: sl });
    if (sl) {
      localStorage.setItem('soapLog', 'true');
    } else {
      localStorage.removeItem('soapLog');
    }
  }

  handleForceStart = () => {
    const { forceStart } = this.state;
    const fs = !forceStart;
    this.setState({ forceStart: fs });
    if (fs) {
      localStorage.setItem('forceStart', 'true');
    } else {
      localStorage.removeItem('forceStart');
    }
  }

  // Helper method to render checkbox options
  renderCheckboxOption = (settingKey, settingData, isChecked) => (
    <FormControlLabel
      key={settingKey}
      control={this.getCheckbox(isChecked, settingData.name)}
      label={settingData.label}
      sx={{ mb: 1 }}
    />
  );

  render() {
    const {
      onClose,
      open,
      whitelist,
      // caller
    } = this.props;
    const {
      psMap,
      pushSettings,
      user,
      soapLog,
      gamDirect,
      forceStart
    } = this.state;

    // console.log('Push Settings:', caller);

    const checkedSettings = {};
    pushSettings.forEach(ps => checkedSettings[ps] = true);
    const showAdvanced = whitelist && whitelist.includes(user);

    return (
      <Dialog
        aria-labelledby="configure-push-dialog-title"
        aria-describedby="configure-push-description"
        open={open}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h4" gutterBottom>
            Push Settings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure which fields to push to GAM for this order
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ mt: 1 }}>
            {/* Main Description */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 3,
                backgroundColor: 'grey.50',
                borderLeft: 4,
                borderLeftColor: 'primary.main'
              }}
            >
              <Typography variant="body2" color="text.secondary">
                This applies only to lines with Ad Manager IDs. Any lines that have not yet been pushed will be pushed in full.
              </Typography>
            </Paper>

            {/* Push Settings Section */}
            <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
              Select Fields to Push
            </Typography>

            <FormControl component="fieldset" fullWidth>
              <FormGroup>
                {/* Select All Option - Highlighted */}
                <Box sx={{ mb: 2 }}>
                  <FormControlLabel
                    control={this.getCheckbox(checkedSettings.selectAll, psMap.get(0).name)}
                    label={
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {psMap.get(0).label}
                      </Typography>
                    }
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      backgroundColor: checkedSettings.selectAll ? 'primary.50' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'grey.50'
                      }
                    }}
                  />
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Individual Settings */}
                <Box sx={{ pl: 2 }}>
                  {Array.from(psMap.entries())
                    .filter(([id]) => id !== 0) // Exclude "Select All"
                    .map(([id, setting]) => (
                      <FormControlLabel
                        key={id}
                        control={this.getCheckbox(checkedSettings[setting.name], setting.name)}
                        label={setting.label}
                        sx={{
                          mb: 1,
                          display: 'block',
                          '& .MuiFormControlLabel-label': {
                            fontSize: '0.95rem'
                          }
                        }}
                      />
                    ))}
                </Box>
              </FormGroup>
            </FormControl>

            {/* Advanced Settings Section */}
            {showAdvanced && (
              <Box sx={{ mt: 4 }}>
                <Divider sx={{ mb: 3 }} />
                <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                  Advanced Settings
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    backgroundColor: 'grey.50',
                    borderRadius: 2
                  }}
                >
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Switch
                          onChange={this.handleChangeDirect}
                          checked={gamDirect}
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body1">Direct to GAM</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Push directly to Google Ad Manager
                          </Typography>
                        </Box>
                      }
                      sx={{ mb: 2 }}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          onChange={this.handleForceStart}
                          checked={!!forceStart}
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body1">Force Start Date to GAM</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Use the GAM start date
                          </Typography>
                        </Box>
                      }
                      sx={{ mb: 2 }}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          onChange={this.handleSoapLog}
                          checked={!!soapLog}
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body1">SOAP Logging</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Enable detailed API request logging
                          </Typography>
                        </Box>
                      }
                      sx={{ mb: 2 }}
                    />
                  </FormGroup>
                </Paper>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={this.pushToGAM}
            sx={{ minWidth: 120 }}
          >
            Push to GAM
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

const mapStateToProps = state => {
  return {}
};

export default connect(mapStateToProps)(PushSettings);
